/**
 * Gratsi x Zelli Instagram Carousel with Infinite Auto-Scroll
 */

console.log('[Gratsi Instagram] Script loaded');

class GratsiInstagramCarousel {
  constructor(container) {
    console.log('[Gratsi Instagram] Initializing carousel...');
    this.container = container;
    this.track = container.querySelector('.gratsi-instagram__carousel-track');
    this.cards = container.querySelectorAll('.gratsi-instagram__card');
    this.autoScroll = container.dataset.autoScroll === 'true';
    this.scrollSpeed = 0.5; // pixels per frame (smooth and slower)
    this.isUserInteracting = false;
    this.animationFrame = null;
    this.originalCardsWidth = 0;
    this.isResetting = false;
    this.scrollAccumulator = 0; // Accumulate fractional pixels for mobile
    
    if (!this.track || !this.cards.length) {
      console.warn('[Gratsi Instagram] Track or cards not found');
      return;
    }
    
    try {
      this.init();
      console.log('[Gratsi Instagram] Carousel initialized successfully');
    } catch (error) {
      console.error('[Gratsi Instagram] Error during initialization:', error);
    }
  }
  
  init() {
    // Calculate original cards width before duplication
    this.calculateOriginalWidth();
    
    // Duplicate cards for seamless infinite scroll
    this.duplicateCards();
    
    // Wait for layout to complete
    setTimeout(() => {
      if (this.autoScroll) {
        // Set initial scroll position to middle set of cards
        this.track.scrollLeft = this.originalCardsWidth;
        this.startAutoScroll();
        console.log('[Gratsi Instagram] Auto-scroll started at position:', this.originalCardsWidth);
      }
    }, 500);
    
    // Setup interaction listeners
    this.setupInteractionListeners();
  }
  
  calculateOriginalWidth() {
    let width = 0;
    this.cards.forEach(card => {
      width += card.offsetWidth + 24; // 24px is the gap
    });
    this.originalCardsWidth = width;
    console.log('Original cards width:', this.originalCardsWidth);
  }
  
  duplicateCards() {
    // Clone cards multiple times (3 sets total) for truly seamless infinite scroll
    const cardsHTML = this.track.innerHTML;
    this.track.innerHTML = cardsHTML + cardsHTML + cardsHTML;
    console.log('Cards duplicated, total scroll width:', this.track.scrollWidth);
  }
  
  startAutoScroll() {
    const scroll = () => {
      if (!this.isUserInteracting && !this.isResetting) {
        // Accumulate fractional pixels
        this.scrollAccumulator += this.scrollSpeed;
        
        // Only apply when we have at least 1 full pixel (for mobile compatibility)
        if (this.scrollAccumulator >= 1) {
          const pixelsToScroll = Math.floor(this.scrollAccumulator);
          this.track.scrollLeft += pixelsToScroll;
          this.scrollAccumulator -= pixelsToScroll;
        }
        
        const currentScroll = this.track.scrollLeft;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        
        // Seamless infinite loop - reset when reaching end of second set
        if (currentScroll >= this.originalCardsWidth * 2) {
          this.isResetting = true;
          this.track.scrollLeft = this.originalCardsWidth;
          this.scrollAccumulator = 0; // Reset accumulator on loop
          setTimeout(() => {
            this.isResetting = false;
          }, 50);
        } 
        // Handle backward scroll
        else if (currentScroll <= 0) {
          this.isResetting = true;
          this.track.scrollLeft = this.originalCardsWidth;
          this.scrollAccumulator = 0; // Reset accumulator on loop
          setTimeout(() => {
            this.isResetting = false;
          }, 50);
        }
      }
      
      this.animationFrame = requestAnimationFrame(scroll);
    };
    
    this.animationFrame = requestAnimationFrame(scroll);
  }
  
  stopAutoScroll() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }
  
  setupInteractionListeners() {
    let scrollTimeout;
    let isDragging = false;
    
    // Pause on hover (desktop)
    this.track.addEventListener('mouseenter', () => {
      this.isUserInteracting = true;
    });
    
    this.track.addEventListener('mouseleave', () => {
      if (!isDragging) {
        this.isUserInteracting = false;
      }
    });
    
    // Track dragging
    this.track.addEventListener('mousedown', () => {
      isDragging = true;
      this.isUserInteracting = true;
    });
    
    this.track.addEventListener('mouseup', () => {
      isDragging = false;
      setTimeout(() => {
        this.isUserInteracting = false;
      }, 500);
    });
    
    // Pause on touch/scroll (mobile)
    this.track.addEventListener('touchstart', () => {
      this.isUserInteracting = true;
    }, { passive: true });
    
    this.track.addEventListener('touchend', () => {
      setTimeout(() => {
        this.isUserInteracting = false;
      }, 2000);
    }, { passive: true });
    
    // Pause on wheel scroll
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Don't interfere with auto-scroll
      }, 100);
    });
  }
  
  destroy() {
    this.stopAutoScroll();
  }
}
/**
 * Fetch tagged articles from a blog using Storefront GraphQL.
 * Requires a public Storefront API token (safe to expose on the client).
 *
 * How to use:
 *   getTaggedArticlesStorefront({ tag: 'pasta', limit: 30 }).then(console.log).catch(console.error);
 *   getTaggedArticlesStorefront({ tag: ['pasta', 'wine'], limit: 30 }).then(console.log).catch(console.error);
 */
async function getTaggedArticlesStorefront({
  blogHandle = 'all',
  tag = 'pasta',
  limit = 20,
  pageSize = 100, // max per request
  shopDomain = 'gratsi.myshopify.com', // or "your-store.myshopify.com"
  storefrontToken = '3f08eeed6b5f34a63ad39d96b50cc823',
  apiVersion = '2025-10' // use a supported version for your store
} = {}) {
  const tags = (Array.isArray(tag) ? tag : String(tag).split(','))
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const API_URL = `https://${shopDomain}/api/${apiVersion}/graphql.json`;

  // GraphQL: fetch articles with fields we care about
  const query = `
    query BlogArticlesByHandle($handle: String!, $pageSize: Int!, $cursor: String) {
      blog(handle: $handle) {
        articles(first: $pageSize, after: $cursor, sortKey: UPDATED_AT, reverse: true) {
          edges {
            cursor
            node {
              id
              handle
              title
              excerpt
              tags
              publishedAt
              instagramVideo: metafield(namespace: "custom", key: "instagram_video") {
                value
                reference {
                  ... on Video {
                    id
                    sources {
                      url
                      mimeType
                      format
                    }
                  }
                }
              }
              instagramLink: metafield(namespace: "custom", key: "instagram_link") {
                value
              }
              onlineStoreUrl
              authorV2 { name }
              image { url altText }
            }
          }
          pageInfo { hasNextPage }
        }
      }
    }
  `;

  async function fetchPage(cursor = null) {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken
      },
      body: JSON.stringify({
        query,
        variables: { handle: blogHandle, pageSize, cursor }
      })
    });

    const json = await res.json();
    if (!res.ok || json?.errors) {
      const msg = json?.errors ? JSON.stringify(json.errors) : `HTTP ${res.status}`;
      throw new Error(`Storefront GraphQL error: ${msg}`);
    }
    return json.data.blog.articles;
  }

  const collected = [];
  let cursor = null;
  let hasNext = true;

  while (hasNext && collected.length < limit) {
    const page = await fetchPage(cursor);

    // Filter by configured tag(s) — article matches if it has any of them
    for (const edge of page.edges) {
      const a = edge.node;
      const articleTags = Array.isArray(a.tags)
        ? a.tags.map((t) => String(t).trim().toLowerCase())
        : [];
      const matchesTag = tags.length === 0 || tags.some((t) => articleTags.includes(t));
      if (matchesTag) {
        // Extract video URL from reference if available
        let videoUrl = null;
        const videoMetafield = a.instagramVideo;
        if (videoMetafield?.reference) {
          // Video reference found, get the first source URL
          const video = videoMetafield.reference;
          if (video.sources && video.sources.length > 0) {
            // Prefer mp4 format, fallback to first available
            const mp4Source = video.sources.find(s => s.format === 'mp4') || video.sources[0];
            videoUrl = mp4Source.url;
          }
        } else if (videoMetafield?.value) {
          // If value is a direct URL (not a GID), use it
          const value = videoMetafield.value;
          if (value && !value.startsWith('gid://')) {
            videoUrl = value;
          }
        }
        
        // Extract instagram_link metafield
        const instagramLink = a.instagramLink?.value || null;
        
        // Get default article URL
        const defaultUrl = a.onlineStoreUrl || `/blogs/${blogHandle}/${a.handle}`;
        
        collected.push({
          id: a.id,
          handle: a.handle,
          title: a.title,
          excerpt: a.excerpt || '',
          tags: a.tags,
          publishedAt: a.publishedAt,
          instagram_video: videoUrl,
          instagram_link: instagramLink,
          author: a.authorV2?.name || null,
          image: a.image ? { url: a.image.url, altText: a.image.altText || '' } : null,
          url: instagramLink || defaultUrl
        });
        if (collected.length >= limit) break;
      }
    }

    hasNext = page.pageInfo.hasNextPage;
    if (hasNext) {
      cursor = page.edges.length ? page.edges[page.edges.length - 1].cursor : null;
      if (!cursor) break; // defensive
    }
  }

  return collected;
}

/**
 * Render article cards dynamically
 */
function renderArticleCards(articles, track) {
  const cardHeights = ['400', '333', '368', '320', '320'];
  let renderedCount = 0;
  
  articles.forEach((article, index) => {
    // Skip articles without image or video
    const hasVideo = article.instagram_video && article.instagram_video.trim() !== '';
    const hasImage = article.image && article.image.url && article.image.url.trim() !== '';
    
    if (!hasVideo && !hasImage) {
      console.log('[Gratsi Instagram] Skipping article without image or video:', article.title);
      return; // Skip this article
    }
    
    const heightIndex = renderedCount % cardHeights.length;
    const cardHeight = cardHeights[heightIndex];
    
    // Create card element
    const card = document.createElement('a');
    card.href = article.url;
    card.className = 'gratsi-instagram__card gratsi-instagram__card--clickable';
    card.setAttribute('data-height', cardHeight);
    card.style.height = `${cardHeight}px`;
    
    // Create card inner container
    const cardInner = document.createElement('div');
    cardInner.className = 'gratsi-instagram__card-inner';
    
    // Check if instagram_video exists
    if (hasVideo) {
      // Create video element
      const video = document.createElement('video');
      video.src = article.instagram_video;
      video.className = 'gratsi-instagram__image';
      video.setAttribute('loading', 'lazy');
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      cardInner.appendChild(video);
    } else if (hasImage) {
      // Create image element
      const img = document.createElement('img');
      img.src = article.image.url;
      img.alt = article.image.altText || article.title;
      img.className = 'gratsi-instagram__image';
      img.setAttribute('loading', 'lazy');
      img.width = 300;
      img.height = 'auto';
      cardInner.appendChild(img);
    }
    
    card.appendChild(cardInner);
    track.appendChild(card);
    renderedCount++;
  });
}

/**
 * Normalize metaobject items (from Liquid JSON) to the same shape as blog articles
 * so renderArticleCards can be reused.
 */
function metaobjectItemsToCards(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, i) => ({
    id: 'metaobject-' + i,
    title: '',
    image: (item.image_url && item.image_url.trim() !== '')
      ? { url: item.image_url, altText: '' }
      : null,
    url: (item.link && item.link.trim() !== '') ? item.link : '#',
    instagram_video: (item.video_url && item.video_url.trim() !== '') ? item.video_url : null
  }));
}

/**
 * Load metaobject items from the section's JSON script tag (when data source is metaobject).
 * Returns array of card-like objects or null if not metaobject source / no data.
 */
function getMetaobjectItemsFromSection(carousel) {
  const sectionId = carousel.dataset.metaobjectSectionId;
  const dataSource = carousel.dataset.dataSource;
  if (dataSource !== 'metaobject' || !sectionId) return null;
  const script = document.getElementById('instagram-carousel-metaobject-' + sectionId);
  if (!script || !script.textContent || !script.textContent.trim()) return null;
  try {
    const items = JSON.parse(script.textContent);
    return Array.isArray(items) ? metaobjectItemsToCards(items) : null;
  } catch (e) {
    console.error('[Gratsi Instagram] Failed to parse metaobject JSON:', e);
    return null;
  }
}

// Initialize carousels when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('[Gratsi Instagram] DOM Content Loaded');
  const carousels = document.querySelectorAll('.gratsi-instagram__carousel');
  console.log('[Gratsi Instagram] Found', carousels.length, 'carousel(s)');
  
  carousels.forEach((carousel, index) => {
    try {
      console.log(`[Gratsi Instagram] Initializing carousel ${index + 1}/${carousels.length}`);
      const track = carousel.querySelector('.gratsi-instagram__carousel-track');
      const articleLimit = parseInt(carousel.dataset.articleLimit) || 20;
      const blogHandle = carousel.dataset.blogHandle || 'all';
      const articleTag = carousel.dataset.articleTag || 'pasta';
      
      if (!track) {
        console.warn('[Gratsi Instagram] Track not found');
        return;
      }
      
      const dataSource = carousel.dataset.dataSource || 'blog';
      const metaobjectCards = dataSource === 'metaobject' ? getMetaobjectItemsFromSection(carousel) : null;
      if (metaobjectCards !== null && metaobjectCards.length > 0) {
        console.log('[Gratsi Instagram] Using metaobject data, items:', metaobjectCards.length);
        renderArticleCards(metaobjectCards, track);
        setTimeout(() => {
          new GratsiInstagramCarousel(carousel);
        }, 100);
        return;
      }
      
      // Blog source: fetch articles and render cards
      if (dataSource !== 'blog') {
        setTimeout(() => { new GratsiInstagramCarousel(carousel); }, 100);
        return;
      }
      getTaggedArticlesStorefront({ limit: articleLimit, blogHandle, tag: articleTag })
        .then(articles => {
          console.log('[Gratsi Instagram] Fetched articles:', articles);
          renderArticleCards(articles, track);
          
          setTimeout(() => {
            new GratsiInstagramCarousel(carousel);
          }, 100);
        })
        .catch(err => {
          console.error('[Gratsi Instagram] Error fetching articles:', err);
        });
    } catch (error) {
      console.error(`[Gratsi Instagram] Error initializing carousel ${index + 1}:`, error);
    }
  });
});

// Re-initialize on Shopify section updates (theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('.gratsi-instagram__carousel');
    if (carousel) {
      new GratsiInstagramCarousel(carousel);
    }
  });
}

